import React from 'react';
import { AlertTriangle } from 'lucide-react';

export type AnalysisQualityStatus =
  | 'rich_intelligence_ready'
  | 'limited_sample'
  | 'insufficient_analysis';

export interface AnalysisQuality {
  status?: AnalysisQualityStatus;
  usable_plays?: number;
  vision_frames_processed?: number;
  parsed_ok?: number;
  reason?: string;
  customer_message?: string;
}

interface ReportAnalysisQualityNoticeProps {
  analysisQuality?: AnalysisQuality | null;
}

export const ReportAnalysisQualityNotice: React.FC<ReportAnalysisQualityNoticeProps> = ({
  analysisQuality,
}) => {
  if (!analysisQuality?.status) {
    return null;
  }

  if (analysisQuality.status === 'rich_intelligence_ready') {
    return null;
  }

  const isInsufficient = analysisQuality.status === 'insufficient_analysis';

  return (
    <div
      className={`card mb-8 border-2 ${
        isInsufficient
          ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30'
          : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
      }`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
            isInsufficient
              ? 'text-red-700 dark:text-red-300'
              : 'text-amber-700 dark:text-amber-300'
          }`}
        />
        <div>
          <p className="font-semibold text-secondary-900 dark:text-white">
            {isInsufficient ? 'Insufficient analysis for a full gameplan' : 'Limited analysis sample'}
          </p>
          <p className="mt-2 text-sm text-secondary-700 dark:text-secondary-300 leading-relaxed">
            {analysisQuality.customer_message ||
              (isInsufficient
                ? 'Scoutlytics could not extract enough readable gameplay from this video to build a reliable scouting report.'
                : 'This report is based on a limited gameplay sample. Confirm tendencies on film before game-planning.')}
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-secondary-600 dark:text-secondary-400">
            {analysisQuality.usable_plays != null && (
              <span>{analysisQuality.usable_plays} usable plays detected</span>
            )}
            {analysisQuality.parsed_ok != null && (
              <span>{analysisQuality.parsed_ok} Vision frames parsed</span>
            )}
          </div>
          {isInsufficient && (
            <p className="mt-3 text-sm font-medium text-secondary-800 dark:text-secondary-200">
              This is not a normal tactical scouting report. Upload a full-game VOD with clear
              scoreboard/HUD visibility for stronger intelligence.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportAnalysisQualityNotice;
